import { HttpClient, HttpErrorResponse, HttpHeaders, HttpStatusCode } from '@angular/common/http';
import { EventEmitter, inject, Injectable } from '@angular/core';
import { SharedNotificationService } from '@luggagehero/shared/services/notification';
import { SharedStorageService } from '@luggagehero/shared/services/storage';
import { SharedWindowService } from '@luggagehero/shared/services/window';
import { catchError, Observable } from 'rxjs';

import { toSafeHeaderValue } from './safe-header-value';
import { SHARED_HTTP_SERVICE_CONFIG_TOKEN } from './shared-http-service.token';

// TODO: Move to shared util
const HTTP_STATUS_CODES = Object.values(HttpStatusCode).filter((code) => typeof code === 'number') as number[];
const isHttpStatusCode = (value: number): value is HttpStatusCode => HTTP_STATUS_CODES.includes(value);

const TRACKING_HEADER_MAP: Record<string, string> = {
  utm_source: 'X-LH-UTM-Source',
  utm_medium: 'X-LH-UTM-Medium',
  utm_campaign: 'X-LH-UTM-Campaign',
  utm_term: 'X-LH-UTM-Term',
  utm_content: 'X-LH-UTM-Content',
  utm_promo_code: 'X-LH-Promo-Code',
  lh_campaign: 'X-LH-Campaign',
  lh_landing_page_origin: 'X-LH-Landing-Page-Origin',
  lh_landing_page_path: 'X-LH-Landing-Page-Path',
  lh_medium: 'X-LH-Medium',
  lh_source: 'X-LH-Source',
  gclid: 'X-LH-GCLID',
  adgroup_id: 'X-LH-Adgroup-ID',
};

// Header names already reported as skipped, so a bad stored value logs once per page load, not per request
const skippedHeadersLogged = new Set<string>();

/**
 * Appends an optional header built from client-side state (storage, the URL, window globals).
 *
 * Every such value goes through `toSafeHeaderValue`: the browser throws while sending a request whose header
 * holds a character above U+00FF, so one bad value would otherwise take down every API call (AM-5103).
 * Angular's HttpHeaders accepts any string; the failure only surfaces in XMLHttpRequest.setRequestHeader or
 * the fetch Headers constructor at send time, so the guard has to sit here, where the value is chosen.
 * A value that still cannot be sent is skipped and logged once, never thrown.
 */
export function appendHeader(headers: HttpHeaders, name: string, value: unknown): HttpHeaders {
  if (typeof value !== 'string' || value.length === 0) {
    return headers;
  }
  try {
    const safeValue = toSafeHeaderValue(value);
    if (safeValue === null) {
      if (!skippedHeadersLogged.has(name)) {
        skippedHeadersLogged.add(name);
        console.warn(`Skipped request header ${name}: its value cannot be sent`);
      }
      return headers;
    }
    return headers.append(name, safeValue);
  } catch {
    // Belt and braces: neither call above throws today. Log the name only, never the value or an error that
    // might carry it (this path also carries Authorization and the booking token).
    if (!skippedHeadersLogged.has(name)) {
      skippedHeadersLogged.add(name);
      console.warn(`Skipped request header ${name}`);
    }
    return headers;
  }
}

// Options subset passed to HttpClient methods — headers and credentials only
type RequestOptions = {
  headers?: HttpHeaders;
  withCredentials?: boolean;
};

@Injectable({
  providedIn: 'root',
})
export class SharedHttpClient {
  public readonly error$: Observable<HttpStatusCode>;

  public readonly client = inject(HttpClient);
  private readonly window = inject(SharedWindowService);
  private readonly notify = inject(SharedNotificationService);
  private readonly storage = inject(SharedStorageService);
  private readonly config = inject(SHARED_HTTP_SERVICE_CONFIG_TOKEN);

  private error = new EventEmitter<HttpStatusCode>();

  constructor() {
    this.error$ = this.error.asObservable();
  }

  public get<T>(url: string, authorize = true, withCredentials = false): Observable<T> {
    const options = this.getRequestOptions(authorize, withCredentials);
    return this.interceptErrors(this.client.get<T>(url, options));
  }

  public postFile<T>(
    url: string,
    file: Blob,
    fileName: string,
    authorize = true,
    withCredentials = false,
  ): Observable<T> {
    const options = this.getRequestOptions(authorize, withCredentials, true);
    const formData = new FormData();
    formData.append('file', file, fileName);
    return this.interceptErrors(this.client.post<T>(url, formData, options));
  }

  public put<T>(url: string, data: unknown, authorize = true, withCredentials = false): Observable<T> {
    const options = this.getRequestOptions(authorize, withCredentials);
    const payload = JSON.stringify(data);
    return this.interceptErrors(this.client.put<T>(url, payload, options));
  }

  public delete<T>(url: string, authorize = true, withCredentials = false): Observable<T> {
    const options = this.getRequestOptions(authorize, withCredentials);
    return this.interceptErrors(this.client.delete<T>(url, options));
  }

  public post<T>(url: string, data: unknown, authorize = true, withCredentials = false): Observable<T> {
    const options = this.getRequestOptions(authorize, withCredentials);
    const payload = JSON.stringify(data);
    return this.interceptErrors(this.client.post<T>(url, payload, options));
  }

  public patch<T>(url: string, data: unknown, authorize = true, withCredentials = false): Observable<T> {
    const options = this.getRequestOptions(authorize, withCredentials);
    const payload = JSON.stringify(data);
    return this.interceptErrors(this.client.patch<T>(url, payload, options));
  }

  private interceptErrors<T>(observable: Observable<T>): Observable<T> {
    return observable.pipe(
      catchError((err: Error, caught: Observable<T>) => {
        this.handleError(err);
        return caught;
      }),
    );
  }

  private handleError(err: unknown): void {
    if (err instanceof HttpErrorResponse) {
      if (err.status === 0) {
        // Client-side connection error
        if (err.error instanceof ProgressEvent) {
          this.notify.error('An error occurred, please check your internet connection');
        }
      } else if (err.status === 503) {
        // Backend deliberately rejected the request as transient (e.g. VAT validation provider
        // down) rather than a bug — surface its message instead of failing silently.
        const body = err.error as { message?: string } | null;
        this.notify.error(body?.message || 'This service is temporarily unavailable, please try again later');
        this.error.emit(err.status);
      } else if (isHttpStatusCode(err.status)) {
        this.error.emit(err.status);
      }
    }

    // Rethrow the error
    throw err;
  }

  private getRequestOptions(authorizeRequest = false, withCredentials = false, isFileUpload = false): RequestOptions {
    // TODO: Remove when we are sure the APIs are fully backwards compatible
    if (this.config.usesLegacyHttpMode()) {
      return this.getLegacyRequestOptions(authorizeRequest, withCredentials);
    }

    let headers = new HttpHeaders({
      Accept: 'application/json',
      'Content-Type': 'application/json; charset=utf-8',
    });

    if (isFileUpload) {
      headers = new HttpHeaders({
        Accept: '*/*',
      });
    }

    if (authorizeRequest && this.storage.token) {
      headers = appendHeader(headers, 'Authorization', `Bearer ${this.storage.token}`);
    }

    headers = appendHeader(headers, 'X-LH-Variant-ID', this.storage.variant);
    headers = appendHeader(headers, 'X-LH-Experiment-ID', this.storage.experimentId);
    headers = appendHeader(headers, 'X-LH-Session-ID', this.storage.sessionId);
    headers = appendHeader(headers, 'X-LH-Visitor-ID', this.storage.visitorId);

    let bookingToken = this.storage.bookingToken;
    try {
      if (this.window?.location?.href) {
        const token = new URL(this.window.location.href).searchParams.get('t');
        if (token && token.length > 0) {
          // A token in the URL wins over the stored one
          bookingToken = token;
        }
      }
    } catch (err) {
      console.error('Error extracting booking token', err);
    }
    headers = appendHeader(headers, 'X-LH-Booking-Token', bookingToken);

    headers = appendHeader(headers, 'X-LH-App-Name', this.window.appName);
    headers = appendHeader(headers, 'X-LH-App-Version', this.window.appVersion);
    headers = appendHeader(headers, 'X-LH-App-Build-ID', this.window.appBuildId);
    headers = appendHeader(headers, 'X-LH-Platform', this.window.platform);

    let trackingData: Record<string, unknown> | null = null;
    try {
      trackingData = this.storage.trackingData;
    } catch (err) {
      // Tracking is optional: a request must never fail because the stored tracking data is unreadable
      console.error('Error reading tracking data', err);
    }
    if (trackingData) {
      const normalizedTrackingData = Object.fromEntries(
        Object.entries(trackingData).map(([k, v]) => [k.toLowerCase(), v]),
      );
      for (const [key, header] of Object.entries(TRACKING_HEADER_MAP)) {
        headers = appendHeader(headers, header, normalizedTrackingData[key]);
      }
    }

    return { headers, withCredentials };
  }

  private getLegacyRequestOptions(authorizeRequest: boolean, withCredentials = false): RequestOptions {
    const options: RequestOptions = { withCredentials };

    if (authorizeRequest) {
      options.headers = new HttpHeaders({
        Accept: 'application/json',
        'Content-Type': 'application/json; charset=utf-8',
        Authorization: `Bearer ${this.storage.token}`,
      });
    }

    return options;
  }
}
